import React, { useState, useEffect, useCallback } from "react";
import { Select, InputNumber, DatePicker, Input, Switch, message, Spin } from "antd";
import { PlusOutlined, DeleteOutlined, InboxOutlined, DollarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { getApiErrorMessage } from "../../../shared/api/errors";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { useTranslation } from "react-i18next";
import usePermissions from "../../../hooks/usePermissions";

const card = { background: "var(--pos-surface)", border: "1px solid var(--pos-border)", borderRadius: 12, padding: "14px 16px" };
const popup = { getPopupContainer: () => document.body, classNames: { popup: { root: "pos-dropdown-popup" } } };

const PosSubBatches = ({ onBack, vendors = [], products = [] }) => {
  const { t, i18n } = useTranslation();
  const { hasPermission, hasAny } = usePermissions();
  const canPay = hasPermission("vendors.payment");
  const canAddExpenses = hasAny(["vendors.expenses_add", "vendors.expenses"]);
  const canCancelExpenses = hasAny(["vendors.expenses_cancel", "vendors.expenses"]);
  const canExpenses = canAddExpenses || canCancelExpenses;
  const currency = readCachedSystemConfig()?.currency_code || "IQD";

  const [mode, setMode] = useState("list"); // list | new | detail
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const vendorOptions = vendors.map((v) => ({ value: v.id, label: v.name || `#${v.id}` }));
  const productOptions = products.map((p) => ({ value: p.id, label: parseLocalizedName(p.name, i18n.language, `#${p.id}`) }));
  const productName = (id) => productOptions.find((p) => p.value === id)?.label || `#${id}`;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/pos/batches");
      setBatches(Array.isArray(r.data) ? r.data : []);
    } catch { setBatches([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (id) => {
    setMode("detail"); setDetail(null);
    try { const r = await apiClient.get(`/pos/batches/${id}`); setDetail(r.data); }
    catch (e) { message.error(getApiErrorMessage(e, undefined, t)); setMode("list"); }
  };

  // ── New intake ─────────────────────────────────────────────────────────
  const [nVendor, setNVendor] = useState(null);
  const [nDate, setNDate] = useState(dayjs());
  const [nRef, setNRef] = useState("");
  const [nLines, setNLines] = useState([{ product_id: null, received_qty: null, is_weight: true, unit_price: null }]);

  const startNew = () => {
    setNVendor(null); setNDate(dayjs()); setNRef("");
    setNLines([{ product_id: null, received_qty: null, is_weight: true, unit_price: null }]);
    setMode("new");
  };
  const setLine = (i, field, val) => setNLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const addLine = () => setNLines((prev) => [...prev, { product_id: null, received_qty: null, is_weight: true, unit_price: null }]);
  const removeLine = (i) => setNLines((prev) => prev.filter((_, idx) => idx !== i));

  const saveIntake = async () => {
    if (!nVendor) { message.error(t("batchSelectFarmer", "Select a farmer")); return; }
    const items = nLines
      .filter((l) => l.product_id && parseFloat(l.received_qty) > 0)
      .map((l) => ({ product_id: l.product_id, received_qty: parseFloat(l.received_qty), is_weight: l.is_weight, unit_price: l.unit_price || null }));
    if (items.length === 0) { message.error(t("batchAddItem", "Add at least one product")); return; }
    setSaving(true);
    try {
      const r = await apiClient.post("/pos/batches", {
        vendor_id: nVendor, received_at: nDate ? nDate.format("YYYY-MM-DD") : null, reference: nRef || null, items,
      });
      message.success(t("batchCreatedOk", "Batch recorded"));
      loadList();
      setDetail(r.data); setMode("detail");
    } catch (e) { message.error(getApiErrorMessage(e, undefined, t)); } finally { setSaving(false); }
  };

  // ── Expenses / pay (detail) ─────────────────────────────────────────────
  const [expType, setExpType] = useState("rent");
  const [expAmount, setExpAmount] = useState(null);
  const [expNote, setExpNote] = useState("");
  const [payAmount, setPayAmount] = useState(null);
  const [busy, setBusy] = useState(false);

  const addExpense = async () => {
    if (!(parseFloat(expAmount) > 0)) { message.error(t("batchEnterAmount", "Enter an amount")); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`/pos/batches/${detail.id}/expenses`, { type: expType, amount: parseFloat(expAmount), note: expNote || null });
      setDetail(r.data); setExpAmount(null); setExpNote(""); loadList();
    } catch (e) { message.error(getApiErrorMessage(e, undefined, t)); } finally { setBusy(false); }
  };
  const delExpense = async (eid) => {
    setBusy(true);
    try { const r = await apiClient.delete(`/pos/batches/${detail.id}/expenses/${eid}`); setDetail(r.data); loadList(); message.success(t("vendorExpenseCancelled", "Expense cancelled")); }
    catch (e) { message.error(getApiErrorMessage(e, undefined, t)); } finally { setBusy(false); }
  };
  const payFarmer = async () => {
    const amt = parseFloat(payAmount);
    if (!(amt > 0)) { message.error(t("batchEnterAmount", "Enter an amount")); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`/pos/batches/${detail.id}/pay`, { amount: amt });
      setDetail(r.data); setPayAmount(null); loadList();
      message.success(t("batchPaymentOk", "Payment recorded"));
    } catch (e) { message.error(getApiErrorMessage(e, undefined, t)); } finally { setBusy(false); }
  };

  const expenseTypes = [
    { value: "rent", label: t("expenseRent", "Rent") },
    { value: "transport", label: t("expenseTransport", "Transport") },
    { value: "moving", label: t("expenseMoving", "Moving") },
    { value: "watching", label: t("expenseWatching", "Watching") },
    { value: "other", label: t("expenseOther", "Other") },
  ];
  const expenseLabel = (v) => expenseTypes.find((x) => x.value === v)?.label || v;

  const th = { textAlign: "start", padding: "8px 10px", fontSize: 11, fontWeight: 800, color: "var(--pos-text-faint)", textTransform: "uppercase" };
  const td = { padding: "8px 10px", color: "var(--pos-text)", borderTop: "1px solid var(--pos-border-soft)" };
  const lbl = { fontSize: 12, color: "var(--pos-text-mute)", marginBottom: 4, display: "block" };

  return (
    <div className="pos-sub-view">
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className="pos-sub-icon"><InboxOutlined /></div>
          <h2 className="pos-sub-title">
            {mode === "new" ? (t("batchNewIntake", "New Intake"))
              : mode === "detail" ? `${t("batch", "Batch")} #${detail?.id ?? ""}`
              : (t("batchesTitle", "Farmer Batches"))}
          </h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {mode === "list" && (
            <button type="button" className="pos-sub-add-btn" onClick={startNew}><PlusOutlined /> {t("batchNewIntake", "New Intake")}</button>
          )}
          {mode !== "list" && (
            <button type="button" className="pos-sub-back" onClick={() => { setMode("list"); loadList(); }}>{t("posBack", "Back")}</button>
          )}
          <button type="button" className="pos-sub-back" onClick={onBack}>{t("posBackToPOS", "Back to POS")}</button>
        </div>
      </div>

      <div className="pos-sub-list">
        {/* ── LIST ── */}
        {mode === "list" && (
          <Spin spinning={loading}>
            {batches.length === 0 ? (
              <div className="pos-sub-empty">
                <div className="pos-sub-empty-icon"><InboxOutlined /></div>
                <div className="pos-sub-empty-title">{t("batchesNone", "No batches yet")}</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {batches.map((b) => (
                  <div key={b.id} className="pos-orders-row" onClick={() => openDetail(b.id)}>
                    <div className="pos-orders-row-left">
                      <div className="pos-orders-id">#{b.id}</div>
                      <div className="pos-orders-meta">
                        <span className="pos-orders-customer">{b.vendor || "—"}</span>
                        <span className="pos-orders-date">
                          {b.received_at ? dayjs(b.received_at).format("YYYY-MM-DD") : ""}
                          {b.reference ? ` · ${b.reference}` : ""}
                          {b.status === "settled" ? ` · ${t("batchSettled", "settled")}` : ""}
                        </span>
                      </div>
                    </div>
                    <div className="pos-orders-row-right">
                      <div className="pos-orders-total">{formatCurrency(b.net_owed, currency)}</div>
                      <div style={{ fontSize: 11, color: b.remaining_owed > 0 ? "#dc2626" : "var(--pos-text-faint)" }}>
                        {t("batchRemainingOwed", "owed")}: {formatCurrency(b.remaining_owed, currency)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Spin>
        )}

        {/* ── NEW INTAKE ── */}
        {mode === "new" && (
          <div style={{ ...card, maxWidth: 860, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
              <label><span style={lbl}>{t("posPurchaseVendor", "Farmer")} *</span>
                <Select showSearch optionFilterProp="label" placeholder={t("batchSelectFarmer", "Select farmer")} value={nVendor} onChange={setNVendor} options={vendorOptions} style={{ width: "100%" }} {...popup} />
              </label>
              <label><span style={lbl}>{t("batchReceivedAt", "Received")}</span>
                <DatePicker value={nDate} onChange={setNDate} allowClear={false} style={{ width: "100%" }} getPopupContainer={() => document.body} popupClassName="pos-dropdown-popup" />
              </label>
              <label><span style={lbl}>{t("batchReference", "Reference")}</span>
                <Input value={nRef} onChange={(e) => setNRef(e.target.value)} placeholder={t("batchReference", "Reference")} />
              </label>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>{t("product", "Product")}</th>
                <th style={th}>{t("batchReceivedQty", "Received")}</th>
                <th style={th}>{t("weight", "By weight")}</th>
                <th style={th}>{t("price", "Price")}</th>
                <th style={th}></th>
              </tr></thead>
              <tbody>
                {nLines.map((l, i) => (
                  <tr key={i}>
                    <td style={td}>
                      <Select showSearch optionFilterProp="label" placeholder={t("product", "Product")} value={l.product_id}
                        onChange={(v) => setLine(i, "product_id", v)} options={productOptions} style={{ width: 240 }} {...popup} />
                    </td>
                    <td style={td}>
                      <InputNumber min={0} value={l.received_qty} onChange={(v) => setLine(i, "received_qty", v)} controls={false} placeholder={l.is_weight ? "kg" : "qty"} style={{ width: 110 }} />
                    </td>
                    <td style={td}>
                      <Switch checked={l.is_weight} onChange={(v) => setLine(i, "is_weight", v)} checkedChildren="kg" unCheckedChildren="qty" />
                    </td>
                    <td style={td}>
                      <InputNumber min={0} value={l.unit_price} onChange={(v) => setLine(i, "unit_price", v)} controls={false} placeholder={t("optional", "optional")} style={{ width: 110 }} />
                    </td>
                    <td style={td}>
                      {nLines.length > 1 && <DeleteOutlined style={{ color: "#dc2626", cursor: "pointer" }} onClick={() => removeLine(i)} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={addLine} style={{ marginTop: 10, border: "1px dashed var(--pos-border-strong)", background: "transparent", color: "var(--pos-accent)", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontWeight: 600 }}>
              <PlusOutlined /> {t("addProduct", "Add product")}
            </button>

            <div style={{ marginTop: 18, textAlign: "end" }}>
              <button type="button" className="pos-sub-add-btn" disabled={saving} onClick={saveIntake}>
                {saving ? (t("posTerminalSaving", "Saving...")) : (t("batchSaveIntake", "Save Intake"))}
              </button>
            </div>
          </div>
        )}

        {/* ── DETAIL ── */}
        {mode === "detail" && (
          !detail ? <div style={{ textAlign: "center", padding: 40 }}><Spin /></div> : (
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16, alignItems: "start" }}>
              {/* Items + expenses */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--pos-border)", fontWeight: 700, color: "var(--pos-text)" }}>
                    {detail.vendor} · {detail.received_at ? dayjs(detail.received_at).format("YYYY-MM-DD") : ""}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={th}>{t("product", "Product")}</th>
                      <th style={{ ...th, textAlign: "end" }}>{t("batchReceivedQty", "Received")}</th>
                      <th style={{ ...th, textAlign: "end" }}>{t("batchSold", "Sold")}</th>
                      <th style={{ ...th, textAlign: "end" }}>{t("batchRemaining", "Remaining")}</th>
                    </tr></thead>
                    <tbody>
                      {detail.items.map((it) => (
                        <tr key={it.id}>
                          <td style={td}>{productName(it.product_id)}</td>
                          <td style={{ ...td, textAlign: "end" }}>{it.received_qty}{it.is_weight ? " kg" : ""}</td>
                          <td style={{ ...td, textAlign: "end" }}>{it.sold}{it.is_weight ? " kg" : ""}</td>
                          <td style={{ ...td, textAlign: "end", fontWeight: 700, color: it.remaining < 0 ? "#dc2626" : "var(--pos-text)" }}>{it.remaining}{it.is_weight ? " kg" : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={card}>
                  <div style={{ fontWeight: 700, color: "var(--pos-text)", marginBottom: 10 }}>{t("batchExpenses", "Expenses")}</div>
                  {detail.expenses.length === 0 ? (
                    <div style={{ color: "var(--pos-text-faint)", fontSize: 13, marginBottom: 10 }}>{t("batchNoExpenses", "No expenses yet")}</div>
                  ) : detail.expenses.map((e) => (
                    <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--pos-border-soft)" }}>
                      <span style={{ color: "var(--pos-text)" }}>
                        {expenseLabel(e.type)}{e.note ? ` — ${e.note}` : ""}
                        {e.cancelled_at && (
                          <span style={{ color: "#e11d48", fontSize: 11, marginInlineStart: 6 }}>
                            ✕ {t("vendorExpenseCancelledBy", "Cancelled")}{e.cancelled_by_name ? ` · ${e.cancelled_by_name}` : ""}
                          </span>
                        )}
                      </span>
                      <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <b style={{ color: "#dc2626", textDecoration: e.cancelled_at ? "line-through" : "none", opacity: e.cancelled_at ? 0.6 : 1 }}>− {formatCurrency(e.amount, currency)}</b>
                        {!e.cancelled_at && canCancelExpenses && <DeleteOutlined style={{ color: "#dc2626", cursor: "pointer" }} title={t("cancel", "Cancel")} onClick={() => delExpense(e.id)} />}
                      </span>
                    </div>
                  ))}
                  {detail.status !== "settled" && canAddExpenses && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      <Select value={expType} onChange={setExpType} options={expenseTypes} style={{ width: 140 }} {...popup} />
                      <InputNumber min={0} value={expAmount} onChange={setExpAmount} controls={false} placeholder={t("amount", "Amount")} style={{ width: 120 }} />
                      <Input value={expNote} onChange={(e) => setExpNote(e.target.value)} placeholder={t("note", "Note")} style={{ width: 160 }} />
                      <button type="button" className="pos-sub-add-btn" disabled={busy} onClick={addExpense}><PlusOutlined /> {t("batchAddExpense", "Add")}</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Settlement */}
              <div style={{ ...card }}>
                <div style={{ fontWeight: 700, color: "var(--pos-text)", marginBottom: 12 }}>{t("batchSettlement", "Settlement")}</div>
                {(() => {
                  const s = detail.settlement;
                  const row = (label, val, color) => (
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", color: color || "var(--pos-text)" }}>
                      <span style={{ color: "var(--pos-text-mute)" }}>{label}</span><b>{val}</b>
                    </div>
                  );
                  // Pay farmer without deducting commission (product gross − expenses − paid).
                  const commission = Number(s.commission) || 0;
                  const netOwed = Math.round(((Number(s.gross) || 0) - (Number(s.expenses) || 0)) * 100) / 100
                    || Math.round(((Number(s.net_owed) || 0) + commission) * 100) / 100;
                  const remaining = Math.round(((Number(s.remaining_owed) || 0) + commission) * 100) / 100;
                  return (<>
                    {row(t("batchGross", "Gross sales"), formatCurrency(s.gross, currency))}
                    {row(t("batchExpenses", "Expenses"), `− ${formatCurrency(s.expenses, currency)}`, "#dc2626")}
                    <div style={{ borderTop: "1px solid var(--pos-border)", margin: "6px 0" }} />
                    {row(t("batchNetOwed", "Net owed"), formatCurrency(netOwed, currency), "#16a34a")}
                    {row(t("batchPaid", "Paid"), `− ${formatCurrency(s.paid, currency)}`)}
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 16 }}>
                      <b>{t("batchRemainingOwed", "Remaining owed")}</b>
                      <b style={{ color: remaining > 0 ? "#dc2626" : "#16a34a" }}>{formatCurrency(remaining, currency)}</b>
                    </div>
                    {detail.status !== "settled" && remaining > 0 && canPay && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <InputNumber min={0} value={payAmount} onChange={setPayAmount} controls={false} placeholder={t("amount", "Amount")} style={{ flex: 1 }} />
                        <button type="button" className="pos-sub-add-btn" disabled={busy} onClick={() => setPayAmount(remaining)}>{t("batchFull", "Full")}</button>
                        <button type="button" className="pos-sub-add-btn" disabled={busy} onClick={payFarmer}><DollarOutlined /> {t("batchPayFarmer", "Pay")}</button>
                      </div>
                    )}
                    {detail.status === "settled" && (
                      <div style={{ marginTop: 10, textAlign: "center", color: "#16a34a", fontWeight: 700 }}>✓ {t("batchSettled", "Settled")}</div>
                    )}
                  </>);
                })()}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PosSubBatches;
